import { TreeNode } from "@/types/tree";
import { getPathsToUnlockedImageControls } from "./controls";
import { BatchRequest } from "../api/api";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { ioTsToOpenAiJsonSchemaResponseFormatValue } from "@/subapps/onboarding/lib/ai";
import { KeywordsForImageInNodeResponseV, SimplifiedTreeNodeForLLM } from "./types";
import { globalStore } from "@/store/store";

// we should simplify it further to make what we pass to the LLM shorter - this will result in a faster response from the LLM
export function simplifyTreeNodeForLLM(node: TreeNode): SimplifiedTreeNodeForLLM {
  return {
    nodeType: node.data.type,
    nodeId: String(node.id),
    content: node.data.properties?.content || {},
    children: node.children.length > 0 
      ? node.children.map(child => simplifyTreeNodeForLLM(child))
      : undefined
  };
}

export function generateImageKeywordsPrompt(section: TreeNode): string {

  console.warn('generateImageKeywordsPrompt called');

  const simplifiedSection = simplifyTreeNodeForLLM(section);
  const imageControls = getPathsToUnlockedImageControls(section);
  const businessName = globalStore.aiSettings.prompt.businessName;
  const businessDescription = globalStore.aiSettings.prompt.businessDescription;
  
  return `You are generating search keywords for stock photo websites (Unsplash/Pexels). 
  
  Based on the following website section structure, please provide search keywords for the images at the specified control paths.

Website Context:
- Business Name: ${businessName}
- Business Description: ${businessDescription}

Section structure:
${jsonStringifyOrUndefined(simplifiedSection)}

Image controls to populate:
${imageControls.map(control => `- NODEID: ${control.nodeId}, CONTROLPATH: ${control.controlPath}`).join('\n')}

STRICT RULES:
1. Keywords must be one or two words.
2. No vague adjectives like "safe", "fun", "professional", and no abstract concepts like "experience", "service"

For each control path, analyze the surrounding content (headings, text, context) AND the business context to determine what the image should represent. Keep the business context in mind as well, if we are building a website for a restaurant, and the section is about making a reservation, the image still needs to related to restaurants, it shouldn't be generic about reservations.

EXAMPLES:
- Dog hotel teaching skiing: "dog", "dog skiing", "ski dog", "snow dog" NOT "outdoor adventure"
- Pizza restaurant: "pizza", "pizza dough", "pizza oven", "pepperoni pizza" NOT "food" or "dough"
- Car repair: "car", "car repair", "car engine", "auto mechanic", NOT "tools" or "service"
- Yoga studio: "yoga studio", "yoga", "yoga pose", "yoga mat"
- Bakery: "bakery", "bread", "croissant"

It's important you return the exact control path specified by me, and the exact node id specified by me (it follows NODEID: - ignore any other id keys in the provided JSON, and the controlpath that follows CONTROLPATH:)

`;
}

export function createImageKeywordsBatchRequest(section: TreeNode): BatchRequest {
  const prompt = generateImageKeywordsPrompt(section);
  
  return {
    id: `image-keywords-${section.id}`,
    messages: [
      {
        role: "user",
        content: prompt
      }
    ],
    responseFormat: ioTsToOpenAiJsonSchemaResponseFormatValue(
      KeywordsForImageInNodeResponseV, 
      "image_keywords_schema"
    )
  };
}